import { useMemo, useState } from "react";
import { BookOpen, MapPin, Search } from "lucide-react";
import { PAYROLL_HELP_TOPICS } from "./payrollModuleHelpContent";
import PayrollHelpUiSnippet from "./PayrollHelpUiSnippet";

export default function PayrollModuleHelp({ darkMode = false }) {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(PAYROLL_HELP_TOPICS[0]?.id || "");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PAYROLL_HELP_TOPICS;
    return PAYROLL_HELP_TOPICS.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q) ||
        t.location.toLowerCase().includes(q) ||
        (t.features || []).some((f) => f.toLowerCase().includes(q))
    );
  }, [query]);

  const active =
    filtered.find((t) => t.id === activeId) || filtered[0] || PAYROLL_HELP_TOPICS[0];

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border ${
        darkMode
          ? "border-[#3C3C4C] bg-[#121212] text-gray-100"
          : "border-gray-200 bg-white text-gray-900"
      }`}
    >
      <div
        className={`shrink-0 border-b px-4 py-3 ${
          darkMode ? "border-[#3C3C4C]" : "border-gray-200"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-redhalo-red dark:text-redhalo-mint" />
            <div>
              <h2 className="text-lg font-bold">Payroll Module Help</h2>
              <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                Module reference — Tax Planning, settings, and request entry points.
              </p>
            </div>
          </div>
          <div className="relative w-full max-w-xs">
            <Search
              className={`absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${
                darkMode ? "text-gray-500" : "text-gray-400"
              }`}
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search features, screens…"
              className={`w-full rounded-none border py-2 pl-8 pr-3 text-xs focus:outline-none focus:ring-2 focus:ring-redhalo-red/30 ${
                darkMode
                  ? "border-[#3C3C4C] bg-[#2C2C2C] text-gray-100"
                  : "border-gray-300 bg-white text-gray-800"
              }`}
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside
          className={`w-56 shrink-0 overflow-y-auto border-r ${
            darkMode ? "border-[#3C3C4C] bg-[#1a1a1a]" : "border-gray-200 bg-gray-50/80"
          }`}
        >
          <div className="space-y-0.5 p-2">
            {filtered.map((topic) => (
              <button
                key={topic.id}
                type="button"
                onClick={() => setActiveId(topic.id)}
                className={`block w-full rounded-none px-3 py-2 text-left text-xs transition-colors ${
                  active?.id === topic.id
                    ? "bg-redhalo-mint/40 font-semibold text-redhalo-navy dark:bg-redhalo-navy/40 dark:text-redhalo-mint"
                    : darkMode
                    ? "text-gray-300 hover:bg-[#2C2C2C]"
                    : "text-gray-700 hover:bg-white"
                }`}
              >
                {topic.title}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className={`px-3 py-4 text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                No topics match your search.
              </p>
            )}
          </div>
        </aside>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {active && (
            <div className="mx-auto max-w-3xl space-y-4">
              <div>
                <h3 className="text-base font-bold">{active.title}</h3>
                <p className={`mt-1 text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                  {active.summary}
                </p>
                <div
                  className={`mt-2 inline-flex items-center gap-1 text-xs ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <MapPin className="h-3 w-3" />
                  {active.location}
                </div>
              </div>
              <PayrollHelpUiSnippet variant={active.snippet} darkMode={darkMode} />
              <ul className={`list-disc space-y-1.5 pl-5 text-sm ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
                {(active.features || []).map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
